// src/audio/sfx.ts
// WebAudio-synthesized sound effects (spec §5.4). NO audio asset files, NO network (§0): every
// sound is generated on the fly from oscillators + gain envelopes. A single AudioContext is created
// lazily and resumed on the first user gesture (browser autoplay policy). All sounds are kept SOFT
// and short — low gain, a gentle low-pass on the master — so they stay kid-friendly. When sound is
// disabled (settings toggle), every method is a no-op.

/** Constructor type for the (optionally webkit-prefixed) AudioContext. */
type AudioCtor = new () => AudioContext;

/** Resolve the AudioContext constructor, tolerating the legacy webkit prefix; null if unavailable. */
function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as Window & typeof globalThis & { webkitAudioContext?: AudioCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Master output level (kept low) and a soft low-pass to round off any harshness. */
const MASTER_GAIN = 0.55;
const LOWPASS_HZ = 2600;

/** A tiny non-zero floor for exponential ramps (which cannot target exactly 0). */
const EPS = 0.0001;

export class Sfx {
  private enabled: boolean;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Set once if context creation fails so we never retry (no fail states, stays silent). */
  private failed = false;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  /** Honor the settings sound toggle. When off, all play methods do nothing. */
  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Create (if needed) and resume the AudioContext. Call from a real user gesture so the browser's
   * autoplay policy lets sound through. No-op when sound is disabled or audio is unavailable.
   */
  resume(): void {
    if (!this.enabled) {
      return;
    }
    const ctx = this.ensureContext();
    if (ctx !== null && ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
  }

  /** Soft 'clack' as a block settles after a slide. */
  slide(): void {
    const a = this.begin();
    if (a === null) {
      return;
    }
    // A short triangle with a quick downward pitch drop reads as a gentle wooden clack;
    // a soft sine adds body without harshness.
    this.blip(a.ctx, a.master, 0, 0.085, 300, 165, 'triangle', 0.07);
    this.blip(a.ctx, a.master, 0, 0.05, 200, 150, 'sine', 0.04);
  }

  /** Gentle 'pop' as a block clears its gate. */
  exit(): void {
    const a = this.begin();
    if (a === null) {
      return;
    }
    // A quick upward chirp = a friendly little pop.
    this.blip(a.ctx, a.master, 0, 0.13, 380, 760, 'sine', 0.075);
  }

  /** Short cheerful rising arpeggio on level complete. */
  win(): void {
    const a = this.begin();
    if (a === null) {
      return;
    }
    // C5 – E5 – G5 – C6 major arpeggio, staggered, then a soft sustained top note.
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => {
      this.blip(a.ctx, a.master, i * 0.1, 0.2, f, f, 'triangle', 0.075);
    });
    this.blip(a.ctx, a.master, notes.length * 0.1, 0.42, 1046.5, 1046.5, 'sine', 0.06);
  }

  /** Release the AudioContext (page teardown). Safe to call more than once. */
  dispose(): void {
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    if (ctx !== null) {
      void ctx.close().catch(() => undefined);
    }
  }

  // ---- internals ----

  /** Lazily build the context + master chain (gain → low-pass → destination). Null on failure. */
  private ensureContext(): AudioContext | null {
    if (this.ctx !== null) {
      return this.ctx;
    }
    if (this.failed) {
      return null;
    }
    const Ctor = audioCtor();
    if (Ctor === null) {
      this.failed = true;
      return null;
    }
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = MASTER_GAIN;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = LOWPASS_HZ;
      master.connect(lp);
      lp.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      return ctx;
    } catch {
      this.failed = true;
      return null;
    }
  }

  /** Common guard for the play methods: enabled + context ready (best-effort resume). */
  private begin(): { ctx: AudioContext; master: GainNode } | null {
    if (!this.enabled) {
      return null;
    }
    const ctx = this.ensureContext();
    if (ctx === null || this.master === null) {
      return null;
    }
    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
    return { ctx, master: this.master };
  }

  /** Schedule one enveloped oscillator note; auto-disconnects when it ends. */
  private blip(
    ctx: AudioContext,
    master: GainNode,
    offset: number,
    dur: number,
    f0: number,
    f1: number,
    type: OscillatorType,
    peak: number,
  ): void {
    const t0 = ctx.currentTime + offset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t0);
    if (f1 !== f0) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + dur);
    }
    const attack = Math.min(0.012, dur * 0.4);
    gain.gain.setValueAtTime(EPS, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    gain.gain.exponentialRampToValueAtTime(EPS, t0 + dur);
    osc.connect(gain);
    gain.connect(master);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
  }
}
